import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "micro-telephone-ne-fonctionne-plus",
  silo: "telephone",
  h1: "Le correspondant ne vous entend plus : que faire quand le micro du téléphone ne fonctionne plus",
  metaTitle: "Micro téléphone ne fonctionne plus : diagnostic",
  metaDescription: "Vos correspondants ne vous entendent plus en appel ? Découvrez les causes possibles d'un micro défaillant et comment vérifier s'il s'agit d'une panne matérielle.",
  excerpt: "\"Allô, tu m'entends ?\" et le silence en retour : un micro défaillant peut venir d'une simple application ou d'un vrai problème matériel. Voici comment faire la différence.",
  publishedAt: "2026-09-09",
  readingMinutes: 5,
  relatedSlugs: ["haut-parleur-telephone-defaillant", "telephone-tombe-dans-eau", "wifi-bluetooth-telephone-ne-se-connecte-plus", "appareil-photo-camera-telephone-en-panne"],
  faq: [
    {
      question: "Pourquoi mes correspondants ne m'entendent plus en appel ?",
      answer: "Les causes les plus fréquentes sont une obstruction physique du micro par de la poussière ou une coque mal ajustée, une application qui bloque l'accès au micro, un accessoire Bluetooth connecté sans que vous le sachiez, ou un dégât lié à un choc ou à l'humidité.",
    },
    {
      question: "Comment savoir si le problème vient du micro ou d'une application spécifique ?",
      answer: "Testez le micro sur plusieurs applications différentes : appel classique, messagerie vocale, dictée vocale, enregistreur. Si le problème n'apparaît que sur une seule application, c'est probablement un réglage logiciel plutôt qu'une panne matérielle.",
    },
    {
      question: "Un téléphone a-t-il plusieurs micros ?",
      answer: "Oui, la plupart des smartphones récents possèdent plusieurs micros, notamment pour la réduction de bruit pendant les appels. Si un seul micro est défaillant, le son peut sembler dégradé sans être totalement absent, ce qui complique parfois le diagnostic.",
    },
    {
      question: "Une coque de protection peut-elle bloquer le micro ?",
      answer: "Oui, c'est plus fréquent qu'on ne le pense. Une coque mal découpée ou légèrement décalée par rapport à l'ouverture du micro peut atténuer voire bloquer complètement le son capté, en particulier après un changement récent de coque.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Vous parlez normalement mais votre interlocuteur n'entend rien, ou seulement des sons hachés : ce type de panne est particulièrement frustrant car il n'est pas toujours perceptible par l'utilisateur lui-même pendant l'appel. Avant de conclure à une panne matérielle, plusieurs vérifications s'imposent.",
    },
    {
      type: "h2",
      text: "Les causes les plus courantes",
    },
    {
      type: "ul",
      items: [
        "Obstruction physique du micro par de la poussière, des peluches ou une protection de coque mal ajustée",
        "Accessoire Bluetooth connecté (écouteurs, kit voiture, enceinte) qui capte le son à votre place, souvent hors de portée",
        "Application spécifique qui n'a pas l'autorisation d'accéder au micro dans les réglages de confidentialité",
        "Dégât lié à un contact avec l'humidité, même léger, ou à un choc récent",
        "Bug logiciel après une mise à jour, qui affecte parfois la gestion audio de certaines applications",
        "Housse de protection ou étui qui recouvre partiellement l'ouverture du micro",
      ],
    },
    {
      type: "h2",
      text: "Comment isoler la cause du problème",
    },
    {
      type: "p",
      text: "La méthode la plus fiable consiste à tester le micro dans différents contextes pour savoir si le problème est général ou limité à une application précise.",
    },
    {
      type: "ol",
      items: [
        "Vérifiez qu'aucun accessoire Bluetooth n'est connecté, même en dehors de sa portée habituelle",
        "Testez un appel téléphonique classique et demandez à votre interlocuteur de confirmer s'il vous entend",
        "Enregistrez un message vocal ou utilisez l'application dictaphone pour tester le micro indépendamment des appels",
        "Vérifiez les autorisations micro accordées à l'application concernée dans les réglages de confidentialité",
        "Retirez la coque de protection et testez à nouveau, pour écarter une obstruction physique",
        "Redémarrez le téléphone pour éliminer un bug logiciel temporaire",
      ],
    },
    {
      type: "callout",
      variant: "tip",
      title: "Le dictaphone, un bon outil de diagnostic",
      text: "Utiliser l'application d'enregistrement vocal de votre téléphone permet de tester rapidement si le micro capte correctement le son, sans dépendre du réseau ni d'un correspondant disponible pour confirmer.",
    },
    {
      type: "h2",
      text: "Micro et haut-parleur : des pannes parfois liées",
    },
    {
      type: "p",
      text: "Si le micro et le haut-parleur posent problème en même temps, la cause est souvent commune : un contact avec l'humidité, un choc récent, ou un problème sur la carte audio de l'appareil. Consultez notre article sur le [haut-parleur défaillant](slug:haut-parleur-telephone-defaillant) si vous constatez aussi des soucis de son en sortie, en plus du micro.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Un micro qui ne fonctionne qu'après une chute ou un contact avec l'eau",
      text: "Si le problème est apparu juste après une chute ou une exposition à l'humidité, il s'agit probablement d'une cause matérielle plutôt que logicielle. Évitez de forcer l'usage du téléphone en mode haut-parleur en attendant le diagnostic.",
    },
    {
      type: "h2",
      text: "Quand consulter un professionnel",
    },
    {
      type: "p",
      text: "Si le micro reste inopérant sur toutes les applications testées, sans lien avec un accessoire Bluetooth ni un réglage de confidentialité, il s'agit probablement d'une panne matérielle : micro obstrué en profondeur, connecteur interne desserré, ou composant endommagé par un choc ou de l'humidité. Un diagnostic technique permet de confirmer l'origine exacte et de proposer une réparation ciblée, sans qu'un remplacement complet du téléphone soit nécessaire.",
    },
  ],
};

export default article;
